// Class strings shared by components and the CMS preview (src/admin/preview.ts,
// bundled for the browser without React), so the preview can't drift from the
// site. Keep this file free of imports.

/** Space between the header and the first line of a page (see PageHero). */
export const pageTop = 'pt-12 md:pt-[52px]'

// Every page title uses one of these. Margins and alignment come from the
// caller (they depend on what's above and below).
const pageTitleSizes = {
  // Short titles: section landing pages, events, campaigns, forms.
  lg: 'text-[35px] leading-10 font-extrabold md:text-[53px] md:leading-[65px]',
  // Longer titles and content-heavy pages (about, veganism, resources…).
  md: 'text-[35px] leading-10 font-extrabold md:text-[48px] md:leading-[58px]',
  // Article titles (blog, press) and dialog headings.
  sm: 'text-[28px] leading-[34px] font-bold md:text-[42px] md:leading-[51px]',
}

export type PageTitleSize = keyof typeof pageTitleSizes

/** Class for a page title, or a heading styled like one. */
export const pageTitleClass = (size: PageTitleSize) =>
  `text-fg ${pageTitleSizes[size]}`

/** The semibold line above a page or article title (Eyebrow `kicker`). */
export const kickerClass =
  'pb-[25px] text-base leading-7 font-semibold text-fg-subtle uppercase'

// Matches the Webflow rich-text look (headings 38/32/24/18, 10px paragraph
// gap, royal bold links, 5px grey-bar blockquotes). Markdown bodies are
// rendered to HTML by src/lib/content/render.ts (via content.server.ts) and may contain raw embeds. Figures
// and captions are styled in app.css (`.rich-text figure`).
const richTextBase =
  'rich-text prose dark:prose-invert max-w-none tracking-[-0.25px] ' +
  'prose-p:my-0 prose-p:mb-2.5 prose-li:my-0 prose-li:marker:text-fg-body prose-ul:mt-0 prose-ul:mb-2.5 prose-ol:mt-0 prose-ol:mb-2.5 ' +
  'prose-headings:font-bold prose-headings:text-fg prose-headings:mb-2.5 prose-ul:pl-10 prose-ol:pl-10 ' +
  'prose-h1:mt-5 prose-h1:text-[38px] prose-h1:leading-[44px] ' +
  'prose-h2:mt-5 prose-h2:text-[32px] prose-h2:leading-9 ' +
  'prose-h3:mt-5 prose-h3:text-2xl prose-h3:leading-[30px] ' +
  'prose-h4:mt-2.5 prose-h4:text-lg prose-h4:leading-6 ' +
  'prose-strong:text-inherit prose-strong:font-bold ' +
  'prose-a:text-accent prose-a:font-bold prose-a:no-underline prose-a:transition-colors ' +
  'prose-blockquote:not-italic prose-blockquote:font-normal prose-blockquote:text-lg prose-blockquote:leading-[22px] ' +
  'prose-blockquote:border-l-[5px] prose-blockquote:border-line prose-blockquote:text-inherit ' +
  'prose-blockquote:my-0 prose-blockquote:mb-2.5 prose-blockquote:py-2.5 prose-blockquote:px-5 ' +
  '[&_blockquote_p]:before:content-none [&_blockquote_p]:after:content-none ' +
  'prose-img:mx-auto prose-img:my-2.5 prose-img:max-w-full ' +
  '[&_iframe]:mx-auto [&_iframe]:my-5 [&_iframe]:aspect-video [&_iframe]:h-auto [&_iframe]:w-full [&_iframe]:max-w-full [&_.yt-facade]:mx-auto [&_.yt-facade]:my-5 [&_.yt-facade]:max-w-full'

// Article (Webflow blog/news body): 18/28 grey at every width. Campaign body:
// 15.6/1.8, dark, with 12px captions and full-width figures (app.css).
const richTextVariants = {
  article: 'text-lg leading-7 text-fg-body [&_blockquote_p:last-child]:mb-0',
  campaign: 'rich-text-campaign text-[15.6px] leading-[1.8em] text-fg-body',
}

/** Class for Markdown-derived HTML with Webflow-faithful typography. */
export const richTextClass = (variant: 'article' | 'campaign') =>
  `${richTextBase} ${richTextVariants[variant]}`

/** One card on /open-rescue (the route and the CMS preview). */
export const rescueClass = {
  title:
    'm-0 text-[22px] leading-7 font-bold text-balance text-fg md:text-[28px] md:leading-9',
  date: 'm-0 pt-2 text-xs tracking-wide text-fg-muted uppercase',
  media: 'overflow-hidden rounded-lg bg-black',
  body: 'pt-5 text-base leading-6 text-fg-body md:text-lg md:leading-[26px] [&_p]:m-0 [&_p+p]:pt-4',
}
